import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from 'store';

import type { ApiData } from 'types/post';
import type { ProgressData } from 'types/progress';
import type { RankData } from 'types/rank';

type State = {
  isInitialize: boolean; // Suspenseと連動させるためのフラグ
  progress?: ProgressData[];
  repeater: boolean;
  onBoarding: number;
  history?: string;
  score: number;
  rank: RankData;
};

type SendData = {
  value: ApiData;
};

const initialState: State = {
  isInitialize: false,
  onBoarding: 1,
  repeater: false,
  score: 0,
  rank: { id: 'regular', label: 'レギュラーランク', range: { min: 0, max: 9 } },
};

const baseUrl = process.env.REACT_APP_API_ENDPOINT || '';

/**
 * userDataApiの設定
 * @typedef {Object} UserDataApiConfig
 * @property {string} reducerPath - リデューサーのパス
 * @property {import('@reduxjs/toolkit/query').BaseQueryFn} baseQuery - ベースクエリ関数
 * @property {Array<string>} tagTypes - タグの種類
 * @property {Object} endpoints - エンドポイントの設定
 */

// RTK Queryの設定
// https://redux-toolkit.js.org/rtk-query/overview
/**
 * ユーザーデータに関するAPIの設定を作成
 * @param {UserDataApiConfig} config - 設定オブジェクト
 * @returns {import('@reduxjs/toolkit/query').Api} - RTK QueryのAPIオブジェクト
 */
export const userDataApi = createApi({
  reducerPath: 'userDataApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      // ここにカスタムヘッダーを追加します
      // 現在の状態にアクセスします
      const state = getState() as RootState;
      // 状態からユーザートークンを取得します
      const userToken = state.liffUser.userToken;
      headers.set('Content-Type', 'application/json');
      headers.set('Authorization', `Bearer ${userToken}`);
      return headers;
    },
  }),
  // ミューテーション後にデータの再取得
  tagTypes: ['User'],
  endpoints: builder => ({
    // データ一式をGET
    /**
     * ユーザーデータを取得する
     * @returns {import('@reduxjs/toolkit/query/react').QueryResponse<ApiData>} - APIのレスポンスデータ
     * @example
     * const response = await getMyData('user123');
     * console.log(response.data); // 取得したユーザーデータ
     */
    getMyData: builder.query<ApiData, undefined>({
      query: () => ({
        url: `/profile`,
        method: 'GET',
      }),
      providesTags: ['User'],
    }),
    // データ一式をPOST
    /**
     * ユーザーデータを送信する
     * @param {SendData} data - 送信データ
     * @returns {import('@reduxjs/toolkit/query/react').MutationResponse<ApiData>} - APIのレスポンスデータ
     * @example
     * const response = await sendMyData({ value: postData });
     * console.log(response.data); // 送信したユーザーデータ
     */
    sendMyData: builder.mutation<ApiData, SendData>({
      query: ({ value }) => ({
        url: `/profile`,
        method: 'POST',
        body: value,
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

// エンドポイント毎にカスタムhooksが作成される
export const { useGetMyDataQuery, useSendMyDataMutation } = userDataApi;
/**
 * ユーザーの状態とアクションを管理するSlice
 * @typedef {import('@reduxjs/toolkit').CaseReducer<State>} CaseReducer
 */
/**
 * ユーザーの状態とアクションを管理するSlice
 * @type {import('@reduxjs/toolkit').Slice<State, CaseReducer>}
 */
const user = createSlice({
  name: 'user',

  initialState,

  reducers: {
    /**
     * repeaterを更新する
     * @param {State} state - 現在の状態
     * @param {PayloadAction<boolean>} action - アクション
     * @example
     * dispatch(updateRepeater(true));
     */
    updateRepeater: (state, action: PayloadAction<boolean>) => {
      state.repeater = action.payload;
    },
    /**
     * historyを更新する
     * @param {State} state - 現在の状態
     * @param {PayloadAction<string>} action - アクション
     * @example
     * dispatch(updateHistory('new history'));
     */
    updateHistory: (state, action: PayloadAction<string>) => {
      state.history = action.payload;
    },
    /**
     * progressを更新する
     * @param {State} state - 現在の状態
     * @param {PayloadAction<ProgressData[]>} action - アクション
     * @example
     * dispatch(updateProgress([{ spotId: '1' }, { spotId: '2' }]));
     */
    updateProgress: (state, action: PayloadAction<ProgressData[]>) => {
      // データを昇順で並べる
      const newData = action.payload.sort(
        (x: ProgressData, y: ProgressData) => +x.spotId - +y.spotId
      );
      state.progress = newData;
    },
    /**
     * scoreを更新する
     * @param {State} state - 現在の状態
     * @param {PayloadAction<number>} action - アクション
     * @example
     * dispatch(updateScore(10));
     */
    updateScore: (state, action: PayloadAction<number>) => {
      state.score = action.payload < 0 ? 0 : state.score + action.payload;
    },
    /**
     * onBoardingを更新する
     * @param {State} state - 現在の状態
     * @param {PayloadAction<number>} action - アクション
     * @example
     * dispatch(updateOnBoarding(2));
     */
    updateOnBoarding: (state, action: PayloadAction<number>) => {
      state.onBoarding = action.payload;
    },
  },
  extraReducers: builder => {
    // アプリを起動した時に状態を取得
    /**
     * ユーザーデータを取得した場合の処理
     * @param {State} state - 現在の状態
     * @param {PayloadAction<ApiData>} action - アクション
     */
    builder.addMatcher(
      userDataApi.endpoints.getMyData.matchFulfilled,
      (state, action: PayloadAction<ApiData>) => {
        console.log('GET user fulfilled');

        const { areaStatusList } = action.payload; // 受け取ったエリア情報の一覧
        const combinedData = {
          ...action.payload,
          areaStatusList, // エリア情報の一覧を結合データに追加
        };

        // ゲーム終了後は必ずオンボーディングをスキップする
        const onBoarding = 0; // オンボーディングスキップ後は `0` に設定する
        // const onBoarding = action.payload.repeater ? 0 : 1; // `repeater` プロパティの値によってオンボーディングの設定を変える場合はコメントを外して使用する

        const progress = action.payload.progress;
        let score = 0;

        // プログレスデータのスコアを計算する
        if (progress && progress.length) {
          score = progress
            .map(
              item =>
                (item.onsite && item.online ? 3 : item.onsite ? 2 : item.online ? 1 : 0) as number
            )
            .reduce((a, b) => a + b);
        }

        // 結果のオブジェクトを作成する
        const result = Object.assign(
          {
            isInitialize: true,
            onBoarding,
            score,
          },
          action.payload
        );

        // 状態を更新して返す
        return {
          ...state,
          ...result,
          ...combinedData,
        };
      }
    );

    // アプリを起動した時に状態を取得: 失敗
    /**
     * ユーザーデータの取得に失敗した場合の処理
     * @param {State} state - 現在の状態
     */
    builder.addMatcher(userDataApi.endpoints.getMyData.matchRejected, state => {
      console.error('GET user rejected');
    });
  },
});

// Action Creators
export const { updateRepeater, updateHistory, updateProgress, updateScore, updateOnBoarding } =
  user.actions;

// Reducer
export default user.reducer;
